import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '../../services/AuthService';

interface PasswordResetFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  onSuccess,
  onCancel
}) => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  
  const navigate = useNavigate();

  // Verify token on component mount
  useEffect(() => {
    if (email && token) {
      verifyToken();
    }
  }, [email, token]);

  const verifyToken = async () => {
    setVerifying(true);
    try {
      await AuthService.verifyResetToken({ email, token });
      setTokenValid(true);
    } catch (err: any) {
      setTokenValid(false);
      setError(err.message || 'Invalid or expired reset token');
    } finally {
      setVerifying(false);
    }
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
    if (!/(?=.*[@$!%*?&])/.test(password)) return 'Password must contain at least one special character';
    return '';
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!email) errors.email = 'Email is required';
    if (!token) errors.token = 'Reset token is required';
    
    const passwordError = validatePassword(password);
    if (passwordError) errors.password = passwordError;
    
    if (!passwordConfirmation) {
      errors.passwordConfirmation = 'Please confirm your password';
    } else if (password !== passwordConfirmation) {
      errors.passwordConfirmation = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');

    try {
      const response = await AuthService.resetPassword({
        email,
        token,
        password,
        password_confirmation: passwordConfirmation
      });
      
      if (response.success) {
        setSuccess(true);
        if (onSuccess) {
          onSuccess();
        } else {
          // Redirect to login after 3 seconds
          setTimeout(() => navigate('/login'), 3000);
        }
      } else {
        setError(response.message || 'Failed to reset password');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/login');
    }
  };

  // Show loading while verifying token
  if (verifying) {
    return (
      <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6">Verifying reset token...</Typography>
        </Box>
      </Paper>
    );
  }

  // Show error if token is invalid
  if (tokenValid === false) {
    return (
      <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Typography variant="h6" gutterBottom>
            Invalid Reset Link
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            This password reset link is invalid or has expired. Please request a new one.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/forgot-password')}
            >
              Request New Link
            </Button>
            <Button
              variant="contained"
              onClick={handleCancel}
            >
              Back to Login
            </Button>
          </Box>
        </Box>
      </Paper>
    );
  }

  // Show success message
  if (success) {
    return (
      <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom color="success.main">
            Password Reset Successful
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Your password has been successfully reset. You can now sign in with your new password.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            size="large"
          >
            Sign In
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Reset Your Password
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Enter your new password below.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          disabled={loading || !!searchParams.get('email')}
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          label="Reset Token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          error={!!validationErrors.token}
          helperText={validationErrors.token || 'This was provided in your reset email'}
          disabled={loading || !!searchParams.get('token')}
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!validationErrors.password}
          helperText={validationErrors.password}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          label="Confirm New Password"
          type={showPasswordConfirmation ? 'text' : 'password'}
          value={passwordConfirmation}
          onChange={(e) => setPasswordConfirmation(e.target.value)}
          error={!!validationErrors.passwordConfirmation}
          helperText={validationErrors.passwordConfirmation}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                  edge="end"
                >
                  {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={loading}
            startIcon={<ArrowBackIcon />}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !password || !passwordConfirmation}
            endIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{ minWidth: 140 }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Box>
      </form>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Remember your password?{' '}
          <Link 
            component="button" 
            variant="body2" 
            onClick={(e) => {
              e.preventDefault();
              navigate('/login');
            }}
          >
            Sign in here
          </Link>
        </Typography>
      </Box>
    </Paper>
  );
};

export default PasswordResetForm;